import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  Spin,
  Alert,
  Empty,
  Segmented,
  Tooltip,
  Collapse,
  Button,
  Modal,
  Table,
  Tag,
} from "antd";
import { InfoCircleOutlined, ReloadOutlined, DownloadOutlined, InstagramOutlined, RedoOutlined } from "@ant-design/icons";
import { message } from "antd";
import dayjs from "dayjs";
import customClientAxios from "../customAxios";
import {
  AdminNurtureTemplates,
  AdminNurtureTemplateStats,
  AdminNurtureFunnel,
  AdminNurtureFunnelContacts,
  AdminNurtureFunnelRetryFailed,
} from "../utils/ApiEndpoints";

// ── Sequence catalog (mirrors NurtureTemplates.js) ──────────────────────────
const SEQUENCES = [
  { key: "prospect",            label: "Prospect" },
  { key: "qualified",           label: "Qualified" },
  { key: "onboarded",           label: "Onboarded" },
  { key: "brand_page",          label: "Brand Page" },
  { key: "event_created",       label: "Event Created" },
  { key: "first_sale",          label: "First Sale" },
  { key: "purchased_retention", label: "Plan Purchased" },
];

// Main Category labels — kept in sync with backend services/crm/categoryMapper.js
const MAIN_CATEGORY_LABELS = {
  music: "Music",
  dance: "Dance",
  arts_crafts: "Arts & Crafts",
  performing_arts: "Performing Arts",
  fitness_sports: "Fitness & Sports",
  wellness: "Wellness",
  food_beverage: "Food & Beverage",
  education: "Education",
  community_social: "Community & Social",
  nightlife_entertainment: "Nightlife & Entertainment",
  travel_adventure: "Travel & Adventure",
  markets_retail: "Markets & Retail",
  other: "Other",
};

// ── Layout & typography ───────────────────────────────────────────────────
const Page = styled.div`
  padding: 24px 32px 80px;
  background: #f8fafc;
  min-height: 100vh;
`;
const Header = styled.div`
  margin-bottom: 16px;
  h1 { font-size: 24px; font-weight: 700; color: #0f172a; margin: 0 0 4px; letter-spacing: -0.3px; }
  p  { color: #64748b; margin: 0; font-size: 13px; }
`;
const TabsBar = styled.div`
  display: flex;
  gap: 2px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 18px;
  overflow-x: auto;
  scrollbar-width: thin;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
`;
const Tab = styled.button`
  position: relative;
  padding: 10px 16px 11px;
  background: transparent;
  border: none;
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  color: ${(p) => (p.$active ? "#0f172a" : "#64748b")};
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
  &:hover { color: #0f172a; }
  &::after {
    content: "";
    position: absolute;
    left: 8px; right: 8px; bottom: -1px;
    height: 2px;
    background: ${(p) => (p.$active ? "linear-gradient(90deg, #6366f1, #8b5cf6)" : "transparent")};
  }
`;
const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

// ── KPI strip ─────────────────────────────────────────────────────────────
const KpiStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 18px;
`;
const KpiCell = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 18px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  ${(p) => p.$clickable && `
    cursor: pointer;
    &:hover {
      border-color: #6366f1;
      box-shadow: 0 4px 14px rgba(99,102,241,0.12);
      transform: translateY(-1px);
    }
  `}
  .label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; margin-bottom: 6px; }
  .value { font-size: 28px; font-weight: 700; color: #0f172a; line-height: 1; }
  .sub   { font-size: 11px; color: #94a3b8; margin-top: 4px; }
`;

// ── Funnel ────────────────────────────────────────────────────────────────
const Section = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px 22px;
  margin-bottom: 18px;
  h3 { font-size: 14px; font-weight: 700; color: #0f172a; margin: 0 0 14px; display: flex; align-items: center; gap: 6px; }
  h3 .hint { font-weight: 400; color: #94a3b8; font-size: 12px; }
`;
const FunnelRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 90px 70px;
  align-items: center;
  gap: 12px;
  padding: 8px 4px;
  font-size: 13px;
  border-radius: 6px;
  transition: background 0.15s ease;
  ${(p) => p.$clickable && `
    cursor: pointer;
    &:hover { background: #f8fafc; }
  `}
  .name  { color: #475569; font-weight: 500; }
  .bar   { background: #f1f5f9; border-radius: 6px; height: 14px; overflow: hidden; }
  .fill  { height: 100%; background: linear-gradient(90deg, #6366f1, #8b5cf6); border-radius: 6px; transition: width 0.3s ease; }
  .fill.failure { background: linear-gradient(90deg, #f87171, #ef4444); }
  .value { font-weight: 700; color: #0f172a; text-align: right; }
  .pct   { color: #64748b; font-size: 12px; text-align: right; }
`;

// ── Tables (per-step + per-category) ───────────────────────────────────────
const TablesRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-bottom: 18px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;
const TableCard = styled(Section)`
  padding: 0;
  overflow: hidden;
  .head {
    padding: 16px 20px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    display: flex; align-items: center; gap: 8px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  th, td {
    padding: 10px 20px;
    text-align: left;
    border-bottom: 1px solid #f8fafc;
  }
  th {
    font-size: 11px;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 600;
    background: #fafbfc;
  }
  td.num { text-align: right; font-variant-numeric: tabular-nums; }
  td.label { color: #1e293b; font-weight: 500; }
  .rate {
    font-size: 11px;
    color: #94a3b8;
    margin-left: 4px;
  }
`;

const LegacySection = styled.div`
  margin-top: 24px;
  .legacy-card {
    background: white;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    padding: 18px;
    margin-bottom: 12px;
    .step  { font-size: 11px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; }
    .title { font-size: 16px; font-weight: 700; color: #1e293b; margin: 2px 0 14px; }
  }
  .legacy-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 16px;
  }
  .legacy-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px solid #f1f5f9;
    font-size: 13px;
    &:last-child { border-bottom: none; }
    .label { color: #64748b; }
    .value { font-weight: 700; color: #1e293b; }
    .rate  { font-size: 11px; color: #94a3b8; margin-left: 6px; }
  }
`;

// ── Helpers ───────────────────────────────────────────────────────────────
function pct(n, d) {
  if (!d || d === 0) return "—";
  return `${((n / d) * 100).toFixed(1)}%`;
}
// CSV cell escaper (RFC 4180-ish): wrap in quotes when value contains
// comma / quote / newline; double-up internal quotes.
function csvCell(v) {
  if (v === null || v === undefined) return "";
  let s = typeof v === "string" ? v : String(v);
  if (/[",\n\r]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
  return s;
}
function fmt(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString();
}

// ── Component ─────────────────────────────────────────────────────────────
export default function NurtureAnalytics() {
  const [sequenceKey, setSequenceKey] = useState("prospect");
  const [window, setWindow] = useState("30d");
  const [funnelData, setFunnelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Legacy per-step cards data (kept per user instruction — "don't delete anything")
  const [legacyTemplates, setLegacyTemplates] = useState([]);
  const [legacyByStep, setLegacyByStep] = useState({});

  // Drill-down modal: which bucket is open + the loaded rows
  const [drilldownBucket, setDrilldownBucket] = useState(null); // null | sent | delivered | read | clicked | converted | failed
  const [drilldownRows, setDrilldownRows] = useState([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // Funnel display mode: "attempts" (every send row) vs "byLead" (deduped).
  // Defaults to byLead — that's the question ops cares about ("how many leads
  // ended up reached?"). Switch back to "attempts" to inspect retry volume.
  const [funnelMode, setFunnelMode] = useState("byLead");
  const openDrilldown = useCallback(async (bucket) => {
    if (!bucket) return;
    setDrilldownBucket(bucket);
    setDrilldownLoading(true);
    setDrilldownRows([]);
    try {
      const { data } = await customClientAxios().get(
        AdminNurtureFunnelContacts(sequenceKey, window, bucket)
      );
      setDrilldownRows(data?.data?.rows || []);
    } catch {
      setDrilldownRows([]);
    } finally {
      setDrilldownLoading(false);
    }
  }, [sequenceKey, window]);

  // Retry all retryable failed sends in the current (sequence, window).
  // Asks for confirmation, calls the backend, surfaces the per-bucket result.
  // Note: looks up the sequence label inline (sequenceLabel const is declared
  // further down the component, so referencing it here would hit the TDZ).
  const handleRetryFailed = useCallback(() => {
    const label =
      SEQUENCES.find((s) => s.key === sequenceKey)?.label || sequenceKey;
    Modal.confirm({
      title: "Retry failed sends",
      content: (
        <div style={{ fontSize: 13, color: "#475569" }}>
          <p>
            This will re-enqueue D1 for every failed send in the{" "}
            <strong>{label}</strong> sequence (window: {window}).
          </p>
          <p style={{ marginTop: 8 }}>
            <strong>Skipped automatically:</strong>
            <br />· Meta error 131026 (number not on WhatsApp)
            <br />· Contacts opted out of nurture
            <br />· Contacts who advanced past this sequence's stage
          </p>
          <p style={{ marginTop: 8, color: "#94a3b8", fontSize: 12 }}>
            Retries fire immediately via the NurtureWorker. Old failed rows
            stay for audit; each retry creates a new sibling row.
          </p>
        </div>
      ),
      okText: "Retry now",
      cancelText: "Cancel",
      onOk: async () => {
        setRetrying(true);
        try {
          const { data } = await customClientAxios().post(
            AdminNurtureFunnelRetryFailed,
            { sequenceKey, stepDay: 1, window }
          );
          const d = data?.data || {};
          message.success(
            `Retry queued: ${d.enqueued || 0} enqueued · ${d.skippedUndeliverable || 0} undeliverable · ${d.skippedStageAdvanced || 0} advanced · ${d.skippedNoPhone || 0} no phone · ${d.skippedOptedOut || 0} opted out`,
            6
          );
        } catch (e) {
          message.error(e?.response?.data?.message || "Retry failed");
        } finally {
          setRetrying(false);
        }
      },
    });
  }, [sequenceKey, window]);

  // Client-side CSV export of the currently-loaded drilldown rows.
  // Filename: nurture_<bucket>_<sequence>_<window>_<date>.csv
  // e.g. nurture_clicked_prospect_30d_2026-05-14.csv
  const downloadDrilldownCsv = useCallback(() => {
    if (!drilldownBucket || !drilldownRows.length) return;
    const cols = [
      ["Name",             (r) => r.name || ""],
      ["Phone",            (r) => r.phoneE164 || ""],
      ["Instagram Handle", (r) => r.instagramHandle || ""],
      ["Instagram URL",    (r) => r.instagramUrl || ""],
      ["Main Category",    (r) => r.mainCategory ? (MAIN_CATEGORY_LABELS[r.mainCategory] || r.mainCategory) : ""],
      ["Stage now",        (r) => r.stage || ""],
      ["Step",             (r) => r.stepDay ? `D${r.stepDay}` : ""],
      ["Event at",         (r) => r.eventAt ? new Date(r.eventAt).toISOString() : ""],
      ...(drilldownBucket === "failed" ? [["Failure reason", (r) => r.failureReason || ""]] : []),
    ];
    const header = cols.map(([h]) => csvCell(h)).join(",");
    const body = drilldownRows.map((r) => cols.map(([, f]) => csvCell(f(r))).join(",")).join("\n");
    // UTF-8 BOM so Excel renders Indian-language names correctly
    const csv = "﻿" + header + "\n" + body + "\n";
    const fname = `nurture_${drilldownBucket}_${sequenceKey}_${window}_${dayjs().format("YYYY-MM-DD")}.csv`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [drilldownBucket, drilldownRows, sequenceKey, window]);

  // ── Fetch the aggregate funnel ────────────────────────────────────────
  const fetchFunnel = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await customClientAxios().get(
        AdminNurtureFunnel(sequenceKey, window)
      );
      setFunnelData(data?.data || null);
    } catch (e) {
      setError(
        e?.response?.data?.error?.message || e?.response?.data?.message || e.message
      );
      setFunnelData(null);
    } finally {
      setLoading(false);
    }
  }, [sequenceKey, window]);

  // ── Fetch legacy per-step data (for the preserved old cards) ─────────
  const fetchLegacy = useCallback(async () => {
    try {
      const list = await customClientAxios().get(AdminNurtureTemplates, {
        params: { sequenceKey },
      });
      const rows = list.data?.data || [];
      setLegacyTemplates(rows);
      const stats = {};
      await Promise.all(
        rows.map(async (r) => {
          try {
            const { data } = await customClientAxios().get(
              AdminNurtureTemplateStats(r.stepDay, sequenceKey),
              { params: { window } }
            );
            stats[r.stepDay] = data?.data || {};
          } catch {
            stats[r.stepDay] = {};
          }
        })
      );
      setLegacyByStep(stats);
    } catch {
      /* legacy view is optional — never block on errors */
    }
  }, [sequenceKey, window]);

  useEffect(() => {
    fetchFunnel();
  }, [fetchFunnel]);

  // Only fetch legacy data when its section is expanded — saves N+1 calls
  // on every tab switch. We trigger via the Collapse onChange.
  const [legacyOpen, setLegacyOpen] = useState([]);
  useEffect(() => {
    if (legacyOpen.includes("legacy")) fetchLegacy();
  }, [legacyOpen, fetchLegacy]);

  const sequenceLabel =
    SEQUENCES.find((s) => s.key === sequenceKey)?.label || sequenceKey;
  const k = funnelData?.kpis || {};
  const funnelAttempts = funnelData?.funnel || {};
  const funnelByLead = funnelData?.byLead || {};
  const perStep = funnelData?.perStep || [];
  const perCategory = funnelData?.perCategory || [];

  // Toggle between "Attempts" (every NurtureMessage row counted) and
  // "Per lead" (deduped by leadId — a lead counts as Sent if ANY of their
  // attempts succeeded; counts as Failed only if ALL their attempts failed).
  // The retry feature creates new sibling rows, so "Per lead" gives the
  // true bottom-line outcome view.
  const funnel = funnelMode === "byLead" ? funnelByLead : funnelAttempts;

  // Maximum "sent" for funnel bar width — use sent so the top stage is 100%
  const funnelMax = funnel.sent || 1;
  const funnelStages = [
    { key: "sent",      label: "Sent",      value: funnel.sent || 0,      basis: funnel.sent || 0,      tone: "default" },
    { key: "delivered", label: "Delivered", value: funnel.delivered || 0, basis: funnel.sent || 0,      tone: "default" },
    { key: "read",      label: "Read",      value: funnel.read || 0,      basis: funnel.delivered || 0, tone: "default" },
    { key: "clicked",   label: "Clicked",   value: funnel.clicked || 0,   basis: funnel.read || 0,      tone: "default" },
    { key: "converted", label: "Converted", value: funnelMode === "byLead" ? 0 : (funnelAttempts.converted || 0), basis: funnel.clicked || 0, tone: "default" },
    // Failed is shown after the happy path — same bar style but red fill.
    // Basis is `sent` so the % reads "X% of sent attempts failed".
    { key: "failed",    label: "Failed",    value: funnel.failed || 0,    basis: funnel.sent || 0,      tone: "failure" },
  ];

  return (
    <Page>
      <Header>
        <h1>Nurture Analytics</h1>
        <p>
          Funnel + per-step + per-category performance. Test sends are excluded
          (only real prospect traffic is counted).
        </p>
      </Header>

      {/* Sequence tabs */}
      <TabsBar>
        {SEQUENCES.map((s) => (
          <Tab
            key={s.key}
            $active={sequenceKey === s.key}
            onClick={() => setSequenceKey(s.key)}
          >
            {s.label}
          </Tab>
        ))}
      </TabsBar>

      {/* Period selector + refresh */}
      <ControlsRow>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          Showing <strong style={{ color: "#0f172a" }}>{sequenceLabel}</strong> sequence
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Segmented
            options={[
              { label: "7d",  value: "7d" },
              { label: "30d", value: "30d" },
              { label: "90d", value: "90d" },
              { label: "All", value: "all" },
            ]}
            value={window}
            onChange={setWindow}
          />
          <Tooltip title="Refetch — picks up new sends and click attribution immediately">
            <Button
              icon={<ReloadOutlined spin={loading} />}
              onClick={() => {
                fetchFunnel();
                if (legacyOpen.includes("legacy")) fetchLegacy();
              }}
              loading={loading}
            >
              Refresh
            </Button>
          </Tooltip>
        </div>
      </ControlsRow>

      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}

      <Spin spinning={loading}>
        {/* ── KPI strip ─────────────────────────────────────────────── */}
        <KpiStrip>
          <KpiCell>
            <div className="label">
              <Tooltip title={`Contacts currently in stages eligible for the ${sequenceLabel} sequence`}>
                Active contacts <InfoCircleOutlined style={{ fontSize: 10 }} />
              </Tooltip>
            </div>
            <div className="value">{fmt(k.activeContacts)}</div>
            <div className="sub">in this sequence's stage</div>
            {k.activeContactsWithPhone != null && (
              <div className="sub">{fmt(k.activeContactsWithPhone)} with phone</div>
            )}
          </KpiCell>
          <KpiCell $clickable onClick={() => openDrilldown("sent")}>
            <div className="label">Sent ({window})</div>
            <div className="value">{fmt(k.sent)}</div>
            <div className="sub">click to view list</div>
          </KpiCell>
          <KpiCell $clickable onClick={() => openDrilldown("clicked")}>
            <div className="label">Clicks</div>
            <div className="value">{fmt(k.clicked)}</div>
            <div className="sub">click to view list</div>
          </KpiCell>
          <KpiCell>
            <div className="label">CTR</div>
            <div className="value" style={{ color: "#6366f1" }}>
              {k.ctr ? `${(k.ctr * 100).toFixed(1)}%` : "—"}
            </div>
            <div className="sub">clicks ÷ sent</div>
          </KpiCell>
          <KpiCell $clickable onClick={() => openDrilldown("converted")}>
            <div className="label">
              <Tooltip title="Of contacts who clicked a nurture in this window, how many have signed up on Growezy (stage = won / activated / purchased). Computed as signed-up clickers ÷ clicks.">
                Conversion <InfoCircleOutlined style={{ fontSize: 10 }} />
              </Tooltip>
            </div>
            <div className="value" style={{ color: "#10b981" }}>
              {k.conversion ? `${(k.conversion * 100).toFixed(1)}%` : "—"}
            </div>
            <div className="sub">{fmt(k.converted)} signed up · click for list</div>
          </KpiCell>
          <KpiCell $clickable={k.failed > 0} onClick={() => k.failed > 0 && openDrilldown("failed")}>
            <div className="label">
              <Tooltip title="Sends that msg91/Meta rejected. Common causes: Meta frequency cap (131049), invalid number, opted-out recipient.">
                Failed <InfoCircleOutlined style={{ fontSize: 10 }} />
              </Tooltip>
            </div>
            <div className="value" style={{ color: k.failed > 0 ? "#ef4444" : "#0f172a" }}>
              {fmt(k.failed)}
            </div>
            <div className="sub">{k.failed > 0 ? "click to view list" : "delivery failures"}</div>
          </KpiCell>
        </KpiStrip>

        {/* ── Funnel ────────────────────────────────────────────────── */}
        <Section>
          <h3 style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <span>
              Funnel{" "}
              <span className="hint">
                {funnelMode === "byLead"
                  ? "— unique leads (deduped, retries collapsed into best outcome)"
                  : "— every send attempt (originals + retries)"}
              </span>
              {typeof funnel.total === "number" && funnel.total > 0 && (
                <span className="hint" style={{ marginLeft: 8 }}>
                  · {fmt(funnel.total)}{" "}
                  {funnelMode === "byLead" ? "unique leads" : "total attempts"}
                  {funnelMode === "attempts" && (
                    <>
                      {" "}· invariant: Sent + Failed + Skipped = Total ({fmt(funnel.sent)} + {fmt(funnel.failed)} + {fmt(funnel.skipped || 0)} = {fmt((funnel.sent || 0) + (funnel.failed || 0) + (funnel.skipped || 0))})
                    </>
                  )}
                </span>
              )}
            </span>
            <Segmented
              size="small"
              value={funnelMode}
              onChange={setFunnelMode}
              options={[
                { label: "Per lead", value: "byLead" },
                { label: "Attempts", value: "attempts" },
              ]}
            />
          </h3>
          {funnel.sent === 0 && funnel.failed === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={`No ${sequenceLabel.toLowerCase()} sends in this window yet`}
              style={{ padding: 24 }}
            />
          ) : (
            funnelStages.map((s) => {
              const clickable = s.value > 0;
              return (
                <FunnelRow
                  key={s.key}
                  $clickable={clickable}
                  onClick={() => clickable && openDrilldown(s.key)}
                >
                  <div className="name" style={s.tone === "failure" ? { color: "#ef4444" } : null}>
                    {s.label}
                  </div>
                  <div className="bar">
                    <div
                      className={`fill ${s.tone === "failure" ? "failure" : ""}`}
                      style={{ width: `${Math.min(100, (s.value / funnelMax) * 100)}%` }}
                    />
                  </div>
                  <div className="value" style={s.tone === "failure" && s.value > 0 ? { color: "#ef4444" } : null}>
                    {fmt(s.value)}
                  </div>
                  <div className="pct">{pct(s.value, s.basis)}</div>
                </FunnelRow>
              );
            })
          )}
        </Section>

        {/* ── Two side-by-side tables ───────────────────────────────── */}
        <TablesRow>
          <TableCard>
            <div className="head">Per step</div>
            {perStep.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" style={{ padding: 24 }} />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Step</th>
                    <th style={{ textAlign: "right" }}>Sent</th>
                    <th style={{ textAlign: "right" }}>CTR</th>
                    <th style={{ textAlign: "right" }}>Failed</th>
                    <th style={{ textAlign: "right" }}>Conv %</th>
                  </tr>
                </thead>
                <tbody>
                  {perStep.map((s) => (
                    <tr key={s.stepDay}>
                      <td className="label">D{s.stepDay}</td>
                      <td className="num">{fmt(s.sent)}</td>
                      <td className="num">{pct(s.clicked, s.sent)}</td>
                      <td className="num" style={s.failed > 0 ? { color: "#ef4444", fontWeight: 600 } : null}>
                        {fmt(s.failed)}
                      </td>
                      <td className="num">{pct(s.converted, s.clicked)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </TableCard>

          <TableCard>
            <div className="head">
              Per Main Category
              <Tooltip title="Performance broken down by the recipient's mainCategory bucket. Useful for spotting which category's image / copy resonates.">
                <InfoCircleOutlined style={{ fontSize: 12, color: "#94a3b8" }} />
              </Tooltip>
            </div>
            {perCategory.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" style={{ padding: 24 }} />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Bucket</th>
                    <th style={{ textAlign: "right" }}>Contacts</th>
                    <th style={{ textAlign: "right" }}>Sent</th>
                    <th style={{ textAlign: "right" }}>CTR</th>
                    <th style={{ textAlign: "right" }}>Failed</th>
                    <th style={{ textAlign: "right" }}>Conv %</th>
                  </tr>
                </thead>
                <tbody>
                  {perCategory.map((c) => (
                    <tr key={c.key || "_unmapped"}>
                      <td className="label">
                        {c.key
                          ? MAIN_CATEGORY_LABELS[c.key] || c.key
                          : <em style={{ color: "#94a3b8" }}>Unmapped</em>}
                      </td>
                      <td className="num">{fmt(c.contactCount)}</td>
                      <td className="num">{fmt(c.sent)}</td>
                      <td className="num">{pct(c.clicked, c.sent)}</td>
                      <td className="num" style={c.failed > 0 ? { color: "#ef4444", fontWeight: 600 } : null}>
                        {fmt(c.failed)}
                      </td>
                      <td className="num">{pct(c.converted, c.clicked)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </TableCard>
        </TablesRow>

        {/* ── Legacy view (collapsed by default per user spec) ─────── */}
        <LegacySection>
          <Collapse
            activeKey={legacyOpen}
            onChange={(keys) => setLegacyOpen(keys)}
            items={[
              {
                key: "legacy",
                label: (
                  <span style={{ fontSize: 13, color: "#475569" }}>
                    Legacy view — original per-step cards (kept for reference)
                  </span>
                ),
                children: (
                  <>
                    <div className="legacy-card">
                      <div className="step">All Steps Combined</div>
                      <div className="title">Funnel total</div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16 }}>
                        <div className="legacy-row"><span className="label">Sent</span><span className="value">{fmt(funnel.sent)}</span></div>
                        <div className="legacy-row">
                          <span className="label">Delivered</span>
                          <span className="value">{fmt(funnel.delivered)}<span className="rate">{pct(funnel.delivered, funnel.sent)}</span></span>
                        </div>
                        <div className="legacy-row">
                          <span className="label">Read</span>
                          <span className="value">{fmt(funnel.read)}<span className="rate">{pct(funnel.read, funnel.delivered)}</span></span>
                        </div>
                        <div className="legacy-row">
                          <span className="label">Clicked</span>
                          <span className="value">{fmt(funnel.clicked)}<span className="rate">{pct(funnel.clicked, funnel.read)}</span></span>
                        </div>
                        <div className="legacy-row">
                          <span className="label">Converted</span>
                          <span className="value">{fmt(funnel.converted)}<span className="rate">{pct(funnel.converted, funnel.clicked)}</span></span>
                        </div>
                      </div>
                    </div>

                    {legacyTemplates.length === 0 ? (
                      <Empty description="No steps configured for this sequence" />
                    ) : (
                      <div className="legacy-grid">
                        {legacyTemplates.map((t) => {
                          const s = legacyByStep[t.stepDay] || {};
                          return (
                            <div className="legacy-card" key={t.stepDay}>
                              <div className="step">Day {t.stepDay}</div>
                              <div className="title">{t.template?.displayName || t.template?.name}</div>
                              <div className="legacy-row"><span className="label">Sent</span><span className="value">{s.sent || 0}</span></div>
                              <div className="legacy-row">
                                <span className="label">Delivered</span>
                                <span className="value">{s.delivered || 0}<span className="rate">{pct(s.delivered, s.sent)}</span></span>
                              </div>
                              <div className="legacy-row">
                                <span className="label">Read</span>
                                <span className="value">{s.read || 0}<span className="rate">{pct(s.read, s.delivered)}</span></span>
                              </div>
                              <div className="legacy-row">
                                <span className="label">Clicked</span>
                                <span className="value">{s.clicked || 0}<span className="rate">{pct(s.clicked, s.read)}</span></span>
                              </div>
                              <div className="legacy-row">
                                <span className="label">Attributed signups</span>
                                <span className="value" style={{ color: "#10b981" }}>{s.attributed_signups || 0}<span className="rate">{pct(s.attributed_signups, s.clicked)}</span></span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ),
              },
            ]}
          />
        </LegacySection>
      </Spin>

      {/* ── Drill-down dialog ────────────────────────────────────────── */}
      <Modal
        open={!!drilldownBucket}
        onCancel={() => setDrilldownBucket(null)}
        footer={null}
        width={900}
        title={
          drilldownBucket
            ? `${drilldownBucket.charAt(0).toUpperCase() + drilldownBucket.slice(1)} — ${sequenceLabel} (${window})`
            : ""
        }
      >
        <Spin spinning={drilldownLoading}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
              gap: 12,
            }}
          >
            <div style={{ fontSize: 12, color: "#64748b" }}>
              {drilldownRows.length} contact{drilldownRows.length === 1 ? "" : "s"}.
              Click a row to open the contact drawer.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {drilldownBucket === "failed" && drilldownRows.length > 0 && (
                <Tooltip title="Re-enqueue D1 for retryable failures. Skips 131026 (number not on WA), opted-out, stage-advanced.">
                  <Button
                    size="small"
                    icon={<RedoOutlined />}
                    onClick={handleRetryFailed}
                    loading={retrying}
                    danger
                  >
                    Retry failed
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Download the current list as CSV with name, phone, Instagram, category, stage, and event time">
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={downloadDrilldownCsv}
                  disabled={!drilldownRows.length}
                  type="primary"
                  ghost
                >
                  Export CSV
                </Button>
              </Tooltip>
            </div>
          </div>
          <Table
            size="small"
            rowKey={(r) => r.messageId}
            dataSource={drilldownRows}
            pagination={{ pageSize: 25, showSizeChanger: false }}
            columns={[
              {
                title: "Name",
                dataIndex: "name",
                key: "name",
                render: (n) => n || <span style={{ color: "#94a3b8" }}>(no name)</span>,
              },
              {
                title: "Phone",
                dataIndex: "phoneE164",
                key: "phoneE164",
                width: 140,
                render: (p) => p ? <code style={{ fontSize: 12 }}>{p}</code> : "—",
              },
              {
                title: "Instagram",
                dataIndex: "instagramHandle",
                key: "instagram",
                width: 160,
                render: (handle, row) => {
                  if (!handle && !row.instagramUrl) {
                    return <span style={{ color: "#cbd5e1" }}>—</span>;
                  }
                  const url = row.instagramUrl || (handle ? `https://www.instagram.com/${handle}/` : null);
                  return (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ fontSize: 12, color: "#c026d3", display: "inline-flex", alignItems: "center", gap: 4 }}
                    >
                      <InstagramOutlined />
                      @{handle || url.split("/").filter(Boolean).pop()}
                    </a>
                  );
                },
              },
              {
                title: "Category",
                dataIndex: "mainCategory",
                key: "mainCategory",
                width: 150,
                render: (c) =>
                  c ? (
                    <Tag color="purple" style={{ margin: 0 }}>
                      {MAIN_CATEGORY_LABELS[c] || c}
                    </Tag>
                  ) : (
                    <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Unmapped</span>
                  ),
              },
              {
                title: "Stage now",
                dataIndex: "stage",
                key: "stage",
                width: 110,
                render: (s) => s ? <Tag style={{ margin: 0 }}>{s}</Tag> : "—",
              },
              {
                title: "Step",
                dataIndex: "stepDay",
                key: "stepDay",
                width: 70,
                render: (d) => d ? `D${d}` : "—",
              },
              {
                title: "Event at",
                dataIndex: "eventAt",
                key: "eventAt",
                width: 160,
                render: (t) =>
                  t ? (
                    <Tooltip title={dayjs(t).format("DD MMM YYYY HH:mm:ss")}>
                      <span style={{ fontSize: 12, color: "#475569" }}>
                        {dayjs(t).fromNow ? dayjs(t).fromNow() : dayjs(t).format("DD MMM HH:mm")}
                      </span>
                    </Tooltip>
                  ) : "—",
                sorter: (a, b) => new Date(a.eventAt) - new Date(b.eventAt),
                defaultSortOrder: "descend",
              },
              ...(drilldownBucket === "failed"
                ? [
                    {
                      title: "Reason",
                      dataIndex: "failureReason",
                      key: "failureReason",
                      render: (r) =>
                        r ? (
                          <Tooltip title={r}>
                            <span style={{ fontSize: 12, color: "#ef4444" }}>
                              {r.length > 50 ? r.slice(0, 50) + "…" : r}
                            </span>
                          </Tooltip>
                        ) : "—",
                    },
                    {
                      title: "Retry",
                      dataIndex: "retry",
                      key: "retry",
                      width: 130,
                      render: (retry) => {
                        if (!retry) {
                          return <span style={{ fontSize: 12, color: "#cbd5e1" }}>not retried</span>;
                        }
                        const statusColor = {
                          queued: "default",
                          sent: "blue",
                          delivered: "cyan",
                          read: "purple",
                          clicked: "green",
                          failed: "red",
                          skipped: "default",
                        }[retry.status] || "default";
                        const tip = [
                          retry.sentAt && `Sent ${dayjs(retry.sentAt).format("DD MMM HH:mm")}`,
                          retry.deliveredAt && `Delivered ${dayjs(retry.deliveredAt).format("DD MMM HH:mm")}`,
                          retry.readAt && `Read ${dayjs(retry.readAt).format("DD MMM HH:mm")}`,
                          retry.clickedAt && `Clicked ${dayjs(retry.clickedAt).format("DD MMM HH:mm")}`,
                        ].filter(Boolean).join(" · ");
                        return (
                          <Tooltip title={tip || `Retried at ${dayjs(retry.createdAt).format("DD MMM HH:mm")}`}>
                            <Tag color={statusColor} style={{ fontSize: 11, margin: 0 }}>
                              {retry.status}
                            </Tag>
                          </Tooltip>
                        );
                      },
                    },
                  ]
                : []),
            ]}
            locale={{ emptyText: <Empty description={`No contacts in '${drilldownBucket}' bucket`} /> }}
          />
        </Spin>
      </Modal>
    </Page>
  );
}
